import React, { useState } from "react";
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  Divider,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { Search, X } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import {
  searchFilings,
  fetchFilingDetail,
  updateSearchParam,
  clearSearchParams,
} from "../reducers/patientFrameSlice";
import NodeViewer from "./NodeViewer";
import LabReportGrid from "./LabReportGrid";

const PathologySearch = () => {
  const dispatch = useDispatch();
  const {
    searchParams,
    searchResults,
    selectedFiling,
    loadingSearch,
    loadingDetail,
    searchError,
  } = useSelector((state) => state.pathologySearch);

  const license_key = useSelector(
    (state) => state.authen.loggedInUser?.license_key,
  );

  const [openDialog, setOpenDialog] = useState(false);

  const columns = [
    {
      field: "emis_id",
      headerName: "EMIS ID",
      flex: 1,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "lab_report_id",
      headerName: "Lab Report Id",
      flex: 1,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "category",
      headerName: "Category",
      flex: 2,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "category_type",
      headerName: "Report Type",
      flex: 2,
      align: "center",
      headerAlign: "center",
      renderCell: (params) =>
        params.value ? "Normal Report" : "Abnormal Report",
    },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "starttime",
      headerName: "Start Time",
      flex: 1,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "notes",
      headerName: "Notes",
      flex: 4,
      align: "center",
      headerAlign: "center",
    },
  ];

  const handleInputChange = (field, value) => {
    dispatch(updateSearchParam({ field, value }));
  };

  const handleSearch = () => {
    if (license_key) {
      dispatch(searchFilings({ searchParams, license_key }));
    }
  };

  const handleClearSearch = () => {
    dispatch(clearSearchParams());
  };

  const handleRowClick = (params) => {
    const lab_report_id = params.row?.lab_report_id;
    const liverun = searchParams.liverun;

    if (lab_report_id && license_key) {
      dispatch(fetchFilingDetail({ lab_report_id, liverun, license_key }));
      setOpenDialog(true);
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
        <Typography
          variant="h5"
          sx={{ mb: 3, fontWeight: "bold", color: "primary.main" }}
        >
          Patient Summary
        </Typography>

        {/* Row 1 */}
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 2 }}>
          <Box>
            <TextField
              label="NHS Number"
              value={searchParams.nhs_number}
              onChange={(e) => handleInputChange("nhs_number", e.target.value)}
              size="small"
            />
          </Box>

          <Box>
            <TextField
              label="Pathology Category"
              value={searchParams.pathology_category}
              onChange={(e) =>
                handleInputChange("pathology_category", e.target.value)
              }
              size="small"
            />
          </Box>
          <Box>
            <TextField
              label="General Keyword"
              value={searchParams.keyword}
              onChange={(e) => handleInputChange("keyword", e.target.value)}
              size="small"
            />
          </Box>

          <Box>
            <TextField
              label="Medication"
              value={searchParams.medication}
              onChange={(e) => handleInputChange("medication", e.target.value)}
              size="small"
            />
          </Box>
          <Box>
            <TextField
              label="Problem Keyword"
              value={searchParams.problem_keyword}
              onChange={(e) =>
                handleInputChange("problem_keyword", e.target.value)
              }
              size="small"
            />
          </Box>
          <Box>
            <TextField
              label="Message Keyword"
              value={searchParams.message_keyword}
              onChange={(e) =>
                handleInputChange("message_keyword", e.target.value)
              }
              size="small"
            />
          </Box>
        </Box>

        {/* Subtest Section */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            Subtest Filters
          </Typography>

          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
            <Box>
              <TextField
                label="Subtest Name"
                value={searchParams.subtest_name}
                onChange={(e) =>
                  handleInputChange("subtest_name", e.target.value)
                }
                size="small"
              />
            </Box>

            <Box>
              <TextField
                label="Min Value"
                type="number"
                value={searchParams.subtest_min}
                onChange={(e) =>
                  handleInputChange("subtest_min", e.target.value)
                }
                size="small"
              />
            </Box>

            <Box>
              <TextField
                label="Max Value"
                type="number"
                value={searchParams.subtest_max}
                onChange={(e) =>
                  handleInputChange("subtest_max", e.target.value)
                }
                size="small"
              />
            </Box>
          </Box>
        </Box>

        {/* Demographics Section */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            Demographics
          </Typography>

          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
            <Box>
              <TextField
                label="Min Age"
                type="number"
                value={searchParams.age_min}
                onChange={(e) => handleInputChange("age_min", e.target.value)}
                size="small"
              />
            </Box>

            <Box>
              <TextField
                label="Max Age"
                type="number"
                value={searchParams.age_max}
                onChange={(e) => handleInputChange("age_max", e.target.value)}
                size="small"
              />
            </Box>

            <Box>
              <FormControl fullWidth size="small">
                <InputLabel>Gender</InputLabel>
                <Select
                  value={searchParams.gender}
                  label="Gender"
                  onChange={(e) => handleInputChange("gender", e.target.value)}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="Male">Male</MenuItem>
                  <MenuItem value="Female">Female</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
              <Button
                variant="outlined"
                startIcon={<X size={18} />}
                onClick={handleClearSearch}
              >
                Clear
              </Button>

              <Button
                variant="contained"
                startIcon={<Search />}
                onClick={handleSearch}
                disabled={loadingSearch}
              >
                {loadingSearch ? "Searching..." : "Search"}
              </Button>
            </Box>
          </Box>
        </Box>

        {/* Buttons */}

        {searchError && (
          <Box sx={{ mt: 2 }}>
            <Typography color="error" variant="body2">
              Error: {searchError}
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Results Section */}
      {(loadingSearch || searchResults.length > 0) && (
        <Paper elevation={3} sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: "bold" }}>
            Search Results {!loadingSearch && `(${searchResults.length})`}
          </Typography>

          {loadingSearch ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <DataGrid
              rows={searchResults}
              columns={columns}
              initialState={{
                pagination: {
                  paginationModel: { pageSize: 10 },
                },
              }}
              pageSizeOptions={[10, 25, 50]}
              onRowClick={handleRowClick}
              autoHeight
              sx={{
                "& .MuiDataGrid-row:hover": {
                  cursor: "pointer",
                  backgroundColor: "#f5f5f5",
                },
              }}
            />
          )}
        </Paper>
      )}

      {/* Filing Detail Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="xl"
        fullWidth
        scroll="paper"
      >
        <DialogTitle sx={{ fontWeight: "bold" }}>
          Filing Details
          <Button
            onClick={handleCloseDialog}
            sx={{ float: "right" }}
            color="error"
            variant="outlined"
          >
            Close
          </Button>
        </DialogTitle>

        <DialogContent dividers>
          {loadingDetail ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
              <CircularProgress />
              <Typography sx={{ ml: 2 }}>Loading filing...</Typography>
            </Box>
          ) : selectedFiling && Object.keys(selectedFiling).length > 0 ? (
            <>
              <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
                <Grid
                  container
                  spacing={2}
                  sx={{
                    mb: 2,
                    alignItems: "stretch",
                  }}
                >
                  {/* Column 1: Patient Data */}
                  <Grid
                    item
                    xs={12}
                    md={3}
                    sx={{
                      pr: 2,
                      borderRight: "2px solid",
                      borderColor: "divider",
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        color: "primary.main",
                        fontWeight: "bold",
                        mb: 1,
                        borderBottom: "2px solid",
                        borderColor: "primary.main",
                        display: "inline-block",
                        pb: 0.5,
                      }}
                    >
                      Patient Data
                    </Typography>

                    <Typography variant="body2">
                      <strong>EMIS ID:</strong>{" "}
                      {selectedFiling.patient_data?.emis_id}
                    </Typography>
                    <Typography variant="body2">
                      <strong>NHS NUM:</strong>{" "}
                      {selectedFiling.patient_data?.nhs_num}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Received Date:</strong>{" "}
                      {selectedFiling.patient_data?.received_date}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Issue Date:</strong>{" "}
                      {selectedFiling.patient_data?.issue_date}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Gender:</strong>{" "}
                      {selectedFiling.patient_data?.gender}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Age:</strong> {selectedFiling.patient_data?.age}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Report ID:</strong>{" "}
                      {selectedFiling.patient_data?.lab_report_id}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Report Type:</strong>{" "}
                      {selectedFiling.patient_data?.report_type}
                    </Typography>
                  </Grid>

                  {/* Column 2: Patient Profile Summary */}
                  <Grid
                    item
                    xs={12}
                    md={3}
                    sx={{
                      px: 2,
                      borderRight: "2px solid",
                      borderColor: "divider",
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        color: "primary.main",
                        fontWeight: "bold",
                        mb: 1,
                        borderBottom: "2px solid",
                        borderColor: "primary.main",
                        display: "inline-block",
                        pb: 0.5,
                      }}
                    >
                      Patient Profile Summary
                    </Typography>

                    <Box sx={{ mt: 1 }}>
                      {selectedFiling.patient_profile?.health_status &&
                        Object.entries(
                          selectedFiling.patient_profile.health_status,
                        ).map(([key, val]) => (
                          <Typography key={key} variant="body2">
                            <strong>{key}:</strong> {val?.value || "-"}{" "}
                            {val?.date ? `(${val.date})` : ""}
                          </Typography>
                        ))}
                    </Box>
                  </Grid>

                  {/* Column 3: Medications */}
                  <Grid
                    item
                    xs={12}
                    md={3}
                    sx={{
                      px: 2,
                      borderRight: "2px solid",
                      borderColor: "divider",
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        color: "primary.main",
                        fontWeight: "bold",
                        mb: 1,
                        borderBottom: "2px solid",
                        borderColor: "primary.main",
                        display: "inline-block",
                        pb: 0.5,
                      }}
                    >
                      Medications
                    </Typography>

                    <Box sx={{ mt: 1, maxWidth: 300 }}>
                      <Typography
                        variant="body2"
                        sx={{ whiteSpace: "normal", wordBreak: "break-word" }}
                      >
                        <strong>Acute:</strong>{" "}
                        {selectedFiling.patient_profile?.medications?.acute
                          ?.length
                          ? selectedFiling.patient_profile.medications.acute.join(
                              ", ",
                            )
                          : "None"}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ whiteSpace: "normal", wordBreak: "break-word" }}
                      >
                        <strong>Repeat:</strong>{" "}
                        {selectedFiling.patient_profile?.medications?.repeat
                          ?.length
                          ? selectedFiling.patient_profile.medications.repeat.join(
                              ", ",
                            )
                          : "None"}
                      </Typography>
                    </Box>
                  </Grid>

                  {/* Column 4: Problems */}
                  <Grid
                    item
                    xs={12}
                    md={3}
                    sx={{
                      pl: 2,
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        color: "primary.main",
                        fontWeight: "bold",
                        mb: 1,
                        borderBottom: "2px solid",
                        borderColor: "primary.main",
                        display: "inline-block",
                        pb: 0.5,
                      }}
                    >
                      Problems
                    </Typography>

                    <Box sx={{ mt: 1 }}>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: "bold", mt: 1 }}
                      >
                        Active
                      </Typography>
                      {selectedFiling.patient_profile?.problems?.active &&
                      Object.keys(
                        selectedFiling.patient_profile.problems.active,
                      ).length > 0 ? (
                        Object.entries(
                          selectedFiling.patient_profile.problems.active,
                        ).map(([problem, date]) => (
                          <Typography key={problem} variant="body2">
                            {problem} {date ? `(${date})` : ""}
                          </Typography>
                        ))
                      ) : (
                        <Typography variant="body2">None</Typography>
                      )}

                      <Typography
                        variant="body2"
                        sx={{ fontWeight: "bold", mt: 1 }}
                      >
                        Past
                      </Typography>
                      {selectedFiling.patient_profile?.problems?.past
                        ?.length ? (
                        selectedFiling.patient_profile.problems.past.map(
                          (p, i) => (
                            <Typography key={i} variant="body2">
                              {p}
                            </Typography>
                          ),
                        )
                      ) : (
                        <Typography variant="body2">None</Typography>
                      )}
                    </Box>
                  </Grid>
                </Grid>

                <Divider sx={{ my: 2 }} />

                <LabReportGrid
                  report={selectedFiling.patient_data?.report_data}
                />

                {Array.isArray(selectedFiling.categories) &&
                selectedFiling.categories.length > 0 ? (
                  selectedFiling.categories.map((cat, idx) => (
                    <Paper
                      key={idx}
                      elevation={1}
                      sx={{
                        p: 2,
                        mb: 2,
                        borderLeft: "5px solid #1976d2",
                        backgroundColor: "#fafafa",
                      }}
                    >
                      <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                        {cat.category_name}
                      </Typography>

                      {cat.current_ruleset ? (
                        <Box sx={{ pl: 2 }}>
                          <NodeViewer
                            root={cat.current_ruleset}
                            nodeConfigs={{}}
                            onSelectNode={() => {}}
                            selectedNodeId={null}
                          />
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No ruleset available.
                        </Typography>
                      )}
                    </Paper>
                  ))
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No categories found in this report.
                  </Typography>
                )}
              </Paper>
            </>
          ) : null}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default PathologySearch;
